import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { CarDriverNoCarDepency } from "../../http/carDrivers/types";
import { getTypesOfUse } from "../../http/cars/types";
import { useOtherCarAssignmentApi } from "../../http/otherCarAssignment/api";
import { getAssignmentTypes, OtherCarAssignment } from "../../http/otherCarAssignment/types";
import { NestedPick, PaginationQuery, PaginationQueryWithFields } from "../../types";
import { formatDateNumeric, isWithinLast48h } from "../../types/utils";
import CarDriverHistoryDialog from "../carDrivers/CarDriverHistoryDialog";
import CreateCarDriverButton from "../carDrivers/CreateCarDriverButton";
import DeleteCarDriverDialog from "../carDrivers/DeleteCarDriverDialog";
import EditCarDriverDialog from "../carDrivers/EditCarDriverDialog";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import SvgHistory from "../icons/History";
import DeleteOtherCarAssignmentDialog from "../otherCarAssignment/DeleteOtherCarAssignmentDialog";
import EditOtherCarAssignmentDialog from "../otherCarAssignment/EditOtherCarAssignmentDialog";
import OtherCarAssignmentHistoryDialog from "../otherCarAssignment/OtherCarAssignmentHistoryDialog";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CustomLink from "../ui/CustomLink";
import NewItemIcon from "../ui/NewItemIcon";
import Table from "../ui/Table";

const CAR_DRIVER_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "startDate",
  "endDate",
  "driver.id",
  "driver.email",
  "driver.fullName",
  "driver.currentCostCenter",
  "typeOfUse",
  "versionNumber",
] as const;
type CarDriverField = (typeof CAR_DRIVER_FIELDS)[number];
type CarDriverSearch = NestedPick<CarDriverNoCarDepency, CarDriverField>;

type CarAssignmentsProps = {
  onUpdate?: () => void;
};

const CarAssignments: FC<CarAssignmentsProps> = ({ onUpdate }) => {
  const i18n = useI18n();
  const filters = useListFilters({ isSearchable: true, prefix: "cd" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const carDriversApi = useCarDriversApi();
  const otherCarAssignmentApi = useOtherCarAssignmentApi();

  const [carDriverToDelete, setCarDriverToDelete] = useState<CarDriverSearch>();
  const [carDriverToEdit, setCarDriverToEdit] = useState<CarDriverSearch>();
  const [carDriverToShowVersions, setCarDriverToShowVersions] = useState<CarDriverSearch>();

  const [otherAssignmentToDelete, setOtherAssignmentToDelete] = useState<OtherCarAssignment>();
  const [otherAssignmentToEdit, setOtherAssignmentToEdit] = useState<OtherCarAssignment>();
  const [otherAssignmentToShowVersions, setOtherAssignmentToShowVersions] = useState<OtherCarAssignment>();

  const { id: carId } = useParams<{ id: string }>();

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const queryDrivers: PaginationQueryWithFields<CarDriverSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_DRIVER_FIELDS,
  };

  const typesOfUse = getTypesOfUse(i18n.t);
  const assTypes = getAssignmentTypes(i18n.t);

  const result = carDriversApi.useAdminCarDrivers(queryDrivers, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const resOther = otherCarAssignmentApi.useAdminOtherCarAssignment(query, aborter.current.signal, carId);
  const [dataOthers] = useDataInitializer(resOther.data, resOther.error);

  const items = getAssignments(data?.items ?? [], dataOthers?.items ?? [], filters.state.orderBy, filters.state.desc);

  const filterOptions = {
    get: carDriversApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <>
      <Table<Assignment>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: <CreateCarDriverButton carId={carId} onUpdate={onUpdate} />,
        }}
        head={
          <>
            <Table.Th title={i18n.t.common.assign} fieldName="assignment.to" order={orderOptions} />
            <Table.Th title={i18n.t.common.costCenter} fieldName="assignment.costCenter" order={orderOptions} />
            <Table.Th title={i18n.t.common.startDate} fieldName="carDriver.startDate" order={orderOptions} />
            <Table.Th title={i18n.t.common.endDate} fieldName="carDriver.endDate" order={orderOptions} />
            <Table.Th
              title={i18n.t.common.typeOfUse}
              fieldName="carDriver.typeOfUse"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
              formatter={(value) => typesOfUse.find((x) => x.value == value)?.label}
            />
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          if (!!item.carDriver) {
            setCarDriverToEdit(item.carDriver);
          }
          if (!!item.otherCarAssignment) {
            setOtherAssignmentToEdit(item.otherCarAssignment);
          }
        }}
        renderRow={(item) =>
          !!item.carDriver ? (
            <>
              <td>
                {isWithinLast48h(item.carDriver.createdAt) && <NewItemIcon />}
                <CustomLink to={`/admin/users/${item.carDriver.driver.id}`}>
                  {item.carDriver.driver.fullName}
                </CustomLink>
              </td>
              <td>{item.carDriver.driver.currentCostCenter}</td>
              <td>{!!item.carDriver.startDate && formatDateNumeric(item.carDriver.startDate, i18n.language)}</td>
              <td>{!!item.carDriver.endDate && formatDateNumeric(item.carDriver.endDate, i18n.language)}</td>
              <td>{typesOfUse.find((x) => x.value == item.carDriver?.typeOfUse)?.label}</td>
            </>
          ) : (
            !!item.otherCarAssignment && (
              <>
                <td>
                  {isWithinLast48h(item.otherCarAssignment.createdAt) && <NewItemIcon />}
                  {assTypes.find((x) => x.value == item.otherCarAssignment?.type)?.label}
                </td>
                <td>{item.otherCarAssignment.costCenter}</td>

                <td>
                  {!!item.otherCarAssignment.startDate &&
                    formatDateNumeric(item.otherCarAssignment.startDate, i18n.language)}
                </td>
                <td>
                  {!!item.otherCarAssignment.endDate &&
                    formatDateNumeric(item.otherCarAssignment.endDate, i18n.language)}
                </td>
                <td></td>
              </>
            )
          )
        }
        rowActions={(item) => [
          {
            onClick: () =>
              !!item.carDriver ? setCarDriverToEdit(item.carDriver) : setOtherAssignmentToEdit(item.otherCarAssignment),
            title: i18n.t.common.edit,
            icon: SvgEdit,
          },
          {
            onClick: () =>
              !!item.carDriver
                ? setCarDriverToShowVersions(item.carDriver)
                : setOtherAssignmentToShowVersions(item.otherCarAssignment),
            title: i18n.t.common.history,
            icon: SvgHistory,
          },
          {
            onClick: () =>
              !!item.carDriver
                ? setCarDriverToDelete(item.carDriver)
                : setOtherAssignmentToDelete(item.otherCarAssignment),
            title: i18n.t.common.delete,
            icon: SvgDelete,
            color: "red",
          },
        ]}
        error={result.error || resOther.error}
        items={items}
        total={items.length}
      />

      {!!carDriverToDelete && (
        <DeleteCarDriverDialog
          carDriverId={carDriverToDelete.id}
          carId={carDriverToDelete.car.id}
          onClose={(success) => {
            setCarDriverToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!carDriverToEdit && (
        <EditCarDriverDialog
          carDriverId={carDriverToEdit.id}
          onClose={(success) => {
            setCarDriverToEdit(undefined);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!carDriverToShowVersions && (
        <CarDriverHistoryDialog
          itemToShowVersionsId={carDriverToShowVersions.id}
          itemVersionToShowVersions={carDriverToShowVersions.versionNumber}
          onClose={() => setCarDriverToShowVersions(undefined)}
        />
      )}

      {!!otherAssignmentToDelete && (
        <DeleteOtherCarAssignmentDialog
          otherCarAssigment={otherAssignmentToDelete}
          onClose={(success) => {
            setOtherAssignmentToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!otherAssignmentToEdit && (
        <EditOtherCarAssignmentDialog
          otherCarAssignmentId={otherAssignmentToEdit.id}
          onClose={(success) => {
            setOtherAssignmentToEdit(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!otherAssignmentToShowVersions && (
        <OtherCarAssignmentHistoryDialog
          itemToShowVersions={otherAssignmentToShowVersions}
          onClose={() => setOtherAssignmentToShowVersions(undefined)}
        />
      )}
    </>
  );
};

export default CarAssignments;

type Assignment = {
  carDriver?: CarDriverSearch;
  otherCarAssignment?: OtherCarAssignment;
};

const getAssignments = (
  drivers: CarDriverSearch[],
  others: OtherCarAssignment[],
  orderBy?: string,
  desc?: boolean,
): Assignment[] => {
  const merged: Assignment[] = [
    ...drivers.map((d) => ({ carDriver: d })),
    ...others.map((o) => ({ otherCarAssignment: o })),
  ];

  const getField = (a: Assignment): string | number => {
    if (orderBy != undefined && orderBy == "assignment.costCenter") {
      const costCenterString = a.carDriver?.driver.currentCostCenter ?? a.otherCarAssignment?.costCenter;
      return Number(costCenterString);
    }
    if (orderBy != undefined && orderBy == "assignment.to") {
      return (a.carDriver?.driver.fullName ?? a.otherCarAssignment?.type ?? "").replace("-", "").toLowerCase();
    }
    return a.carDriver?.startDate ?? a.otherCarAssignment!.startDate;
  };

  const isDesc = desc == undefined ? false : !desc;

  merged.sort((a, b) => {
    const aVal = getField(a);
    const bVal = getField(b);

    if (isDesc) {
      if (aVal < bVal) return 1;
      if (aVal > bVal) return -1;
    } else {
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
    }

    return 0;
  });

  return merged;
};
